import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Megaphone, CheckCircle, Info, AlertTriangle, ShieldCheck, Check } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';

const TYPE_CONFIG = {
    info: {
        icon: Info,
        gradient: 'from-sky-500 to-blue-600',
        bg: 'bg-sky-500/10',
        text: 'text-sky-400',
        border: 'border-sky-500/20'
    },
    warning: {
        icon: AlertTriangle,
        gradient: 'from-amber-500 to-orange-600',
        bg: 'bg-amber-500/10',
        text: 'text-amber-400',
        border: 'border-amber-500/20'
    },
    promo: {
        icon: Megaphone,
        gradient: 'from-violet-500 to-fuchsia-600',
        bg: 'bg-violet-500/10',
        text: 'text-violet-400',
        border: 'border-violet-500/20'
    }
};

const BroadcastListener = () => {
    const { user, profile } = useAuth();
    const [unreadBroadcasts, setUnreadBroadcasts] = useState([]);
    const [currentBroadcast, setCurrentBroadcast] = useState(null);
    const [loading, setLoading] = useState(false);
    const location = useLocation();

    // Disable broadcasts on auth and onboarding pages
    const isPublicOrOnboardingPath = ['/login', '/register', '/onboarding', '/reset-password', '/forgot-password'].some(p => location.pathname.startsWith(p));

    useEffect(() => {
        if (!user || !profile || isPublicOrOnboardingPath) return;

        const fetchBroadcasts = async () => {
            try {
                // Determine user target category
                let myTarget = profile.role;
                if (profile.role === 'agent' && profile.organization_id) {
                    myTarget = 'soloAgent';
                }

                // Get broadcasts targeting 'all' or this user's specific role
                // We use a query to find all active broadcasts, then filter out the read ones.
                const { data: broadcasts, error: bError } = await supabase
                    .from('broadcasts')
                    .select('*')
                    .or(`target_role.is.null,target_role.eq.${myTarget}`)
                    .order('sent_at', { ascending: false })
                    .limit(10);

                if (bError) throw bError;
                if (!broadcasts || broadcasts.length === 0) return;

                // Get reads for this user
                const { data: reads, error: rError } = await supabase
                    .from('broadcasts')
                    .select('id, broadcast_reads!inner(user_id)')
                    .eq('broadcast_reads.user_id', user.id);

                if (rError) throw rError;

                const readIds = new Set((reads || []).map(r => r.id));
                const unread = broadcasts.filter(b => !readIds.has(b.id));

                if (unread.length > 0) {
                    setUnreadBroadcasts(unread);
                    // Show the oldest unread first
                    setCurrentBroadcast(unread[unread.length - 1]);
                }
            } catch (e) {
                console.error("Errore caricamento broadcast:", e);
            }
        };

        fetchBroadcasts();

        // Optional: subscribe to new broadcasts
        const subscription = supabase
            .channel('public:broadcasts')
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'broadcasts' }, (payload) => {
                const newB = payload.new;
                const myTarget = profile.role === 'agent' && profile.organization_id ? 'soloAgent' : profile.role;
                if (!newB.target_role || newB.target_role === myTarget) {
                    setUnreadBroadcasts(prev => [...prev, newB]);
                    if (!currentBroadcast) setCurrentBroadcast(newB);
                }
            })
            .subscribe();

        return () => {
            supabase.removeChannel(subscription);
        };
    }, [user?.id, profile?.role, profile?.organization_id]);

    const handleMarkAsRead = async () => {
        if (!currentBroadcast || loading) return;
        setLoading(true);
        try {
            await supabase.from('broadcast_reads').insert({
                broadcast_id: currentBroadcast.id,
                user_id: user.id
            });

            // Move to next
            const nextList = unreadBroadcasts.filter(b => b.id !== currentBroadcast.id);
            setUnreadBroadcasts(nextList);
            setCurrentBroadcast(nextList.length > 0 ? nextList[nextList.length - 1] : null);

        } catch (e) {
            console.error("Errore salvataggio lettura:", e);
            toast.error("Errore di connessione. Riprova.");
        } finally {
            setLoading(false);
        }
    };

    if (!currentBroadcast) return null;

    const config = TYPE_CONFIG[currentBroadcast.type] || TYPE_CONFIG.info;
    const Icon = config.icon;

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                <motion.div
                    initial={{ scale: 0.9, opacity: 0, y: 20 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    className="relative max-w-md w-full bg-[#13111f] rounded-3xl overflow-hidden shadow-2xl border border-white/10"
                >
                    {/* Header Strip */}
                    <div className={`h-2 w-full bg-gradient-to-r ${config.gradient}`} />

                    <div className="p-8 space-y-6">
                        {/* Icon & Title */}
                        <div className="flex flex-col items-center text-center space-y-4">
                            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${config.bg} border ${config.border}`}>
                                <Icon className={`w-8 h-8 ${config.text}`} />
                            </div>

                            <h2 className="text-xl font-bold text-white leading-tight">
                                {currentBroadcast.subject}
                            </h2>
                        </div>

                        {/* Message Body */}
                        <div className="text-gray-300 text-sm leading-relaxed p-4 bg-white/5 rounded-2xl border border-white/5 max-h-[40vh] overflow-y-auto custom-scrollbar whitespace-pre-wrap text-center">
                            {currentBroadcast.message}
                        </div>

                        {/* Date info */}
                        <div className="text-xs text-gray-500 text-center font-medium">
                            Comunicazione del {new Date(currentBroadcast.sent_at).toLocaleDateString('it-IT')}
                        </div>

                        {/* Action List */}
                        <div className="pt-2">
                            <button
                                onClick={handleMarkAsRead}
                                disabled={loading}
                                className={`w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold text-white transition-all shadow-lg hover:shadow-xl hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100 bg-gradient-to-r ${config.gradient}`}
                            >
                                {loading ? (
                                    <span className="animate-pulse">Registrazione...</span>
                                ) : (
                                    <>
                                        <CheckCircle className="w-5 h-5" />
                                        <span>Ho letto e compreso</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

export default BroadcastListener;
